import { useLabels } from "~/hooks/useLabels";
import { tok } from "~/utils/style";
import { getFullUrl } from "~/utils/url";
import { HELP_CENTER_URL } from "~/constants";

import { Heading } from "../heading/Heading";
import { Text } from "../text/Text";
import { When } from "../when/When";
import { ButtonStack } from "../button/ButtonStack";
import { Button } from "../button/Button";
import { Stack } from "../stack/Stack";

import "./ErrorView.css";

export interface ErrorViewProps {
  artwork?: string;
  status?: Response["status"];
}

const DEFAULT_LABELS = {
  "error.default.heading": "Something went wrong...",
  "error.default.description":
    "We're working on fixing the problem. Please try again.",
  "error.page_not_found.heading": "Page Not Found",
  "error.page_not_found.description":
    "We couldn't find the page you're looking for.",
  "error.cta.home": "Go Home",
  "error.cta.help_center": "Help Center",
};

function isServerError(status: number) {
  return status >= 500;
}

export function ErrorView({ artwork, status = 500 }: ErrorViewProps) {
  const { t } = useLabels({ fallback: DEFAULT_LABELS });

  const artworkSrc =
    artwork || isServerError(status)
      ? "/images/error-occured-wide.png"
      : "/images/grief-wide.png";

  const heading = isServerError(status)
    ? t("error.default.heading")
    : t("error.page_not_found.heading");

  const description = isServerError(status)
    ? t("error.default.description")
    : t("error.page_not_found.description");

  return (
    <div className="ErrorView">
      <Stack gap={tok("size", "2")}>
        <When condition={Boolean(artworkSrc)}>
          <img
            className="ErrorView-artwork"
            src={getFullUrl(artworkSrc)}
            alt={heading}
          />
        </When>

        <div className="ErrorView-body">
          <Heading level="1">{heading}</Heading>
          <Text as="p">{description}</Text>
        </div>

        <When condition={!isServerError(status)}>
          <ButtonStack>
            <Button
              href={getFullUrl("/", { includeBasePath: false })}
              target="_self"
              appearance="primary"
              fill
            >
              {t("error.cta.home")}
            </Button>

            <Button href={HELP_CENTER_URL} appearance="secondary" fill>
              {t("error.cta.help_center")}
            </Button>
          </ButtonStack>
        </When>
      </Stack>
    </div>
  );
}
